// src/components/ReferralPageModal.jsx
import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useNotifications } from "../hooks/notificationsContext";
import { getAuth } from "firebase/auth";
import { getUserData } from "../services/userService";
import { getNextZ } from "../utils/zIndexManager";
import "./modalStyles.css";

export default function ReferralPageModal({ onClose }) {
  const { pushNotification } = useNotifications();
  const [loading, setLoading] = useState(true);
  const [collecting, setCollecting] = useState(false);
  const [referralData, setReferralData] = useState(null);

  const modalRef = useRef(null);
  const [dragData, setDragData] = useState({ isDragging: false, offsetX: 0, offsetY: 0 });
  const [zIndex, setZIndex] = useState(() => getNextZ() + 10000);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const modal = modalRef.current;
    if (modal) {
      const { innerWidth, innerHeight } = window;
      const { offsetWidth, offsetHeight } = modal;
      setPosition({
        top: innerHeight / 2 - offsetHeight / 2,
        left: innerWidth / 2 - offsetWidth / 2,
      });
    }
  }, []);

  const handleMouseDown = (e) => {
    const modal = modalRef.current;
    if (!modal) return;

      setZIndex(getNextZ() + 10000);

    const rect = modal.getBoundingClientRect();
    setDragData({
      isDragging: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (!dragData.isDragging) return;
    setPosition({
      top: e.clientY - dragData.offsetY,
      left: e.clientX - dragData.offsetX,
    });
  };

  const handleMouseUp = () => {
    if (dragData.isDragging) setDragData((d) => ({ ...d, isDragging: false }));
  };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  useEffect(() => {
    const load = async () => {
      try {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        if (!uid) {
          pushNotification?.({ type: "error", message: "No authenticated user." });
          return;
        }
        const data = await getUserData(uid);
        setReferralData(data);
      } catch (err) {
        console.error(err);
        pushNotification?.({ type: "error", message: "Failed to load referral data." });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [pushNotification]);

  const handleCollect = async () => {
    try {
      setCollecting(true);
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        pushNotification?.({ type: "error", message: "You must be signed in." });
        return;
      }
      const token = await user.getIdToken();

      const res = await fetch(
        "https://redeemreferralpayout-5dhfwyjvzq-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(result?.message || "Redeem failed");

      pushNotification?.({
        type: "success",
        message: result?.message || "Referral payout collected!",
      });

      const updatedData = await getUserData(user.uid);
      setReferralData(updatedData);
    } catch (err) {
      console.error(err);
      pushNotification?.({
        type: "error",
        message: err?.message || "Failed to collect referral payout.",
      });
    } finally {
      setCollecting(false);
    }
  };

  const formatCurrency = (val) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(val || 0));

  const modalContent = (
    <div
      ref={modalRef}
      className="floating-modal-container"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: "400px",
        minHeight: "300px",
        transform: "none",
        zIndex,
      }}
    >
      <div className="draggable-title" onMouseDown={handleMouseDown}>
        <span style={{ color: "#50E3C2" }}>Referrals</span>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="scrollable-content">
        {loading ? (
          <p>Loading...</p>
        ) : !referralData ? (
          <p>No referral data found.</p>
        ) : (
          <table className="currency-exchange-table">
            <tbody>
              <tr>
                <td>Total Referrals</td>
                <td style={{ color: "#fff" }}>{referralData.userReferrals}</td>
              </tr>
              <tr>
                <td>Lifetime Referrals</td>
                <td style={{ color: "#fff" }}>{formatCurrency(referralData.userLifetimeReferrals)}</td>
              </tr>
              <tr>
                <td>Referral Payout</td>
                <td style={{ color: "#fff" }}>{formatCurrency(referralData.userReferralPayout)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </div>

      <div
        className="modal-footer"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "12px",
          gap: "8px",
        }}
      >
        <button
          className="settings-button"
          onClick={handleCollect}
          disabled={loading || collecting || referralData?.userReferralPayout <= 0}
          title={
            collecting
              ? "Collecting..."
              : referralData?.userReferralPayout <= 0
              ? "No earnings to collect"
              : "Collect Earnings"
          }
          style={{
            width: "25%",
            backgroundColor: referralData?.userReferralPayout > 0 ? "#50E3C2" : "#888",
            color: "#fff",
            cursor: referralData?.userReferralPayout > 0 ? "pointer" : "default",
            opacity: referralData?.userReferralPayout > 0 ? 1 : 0.6,
          }}
        >
          {collecting ? "Collecting..." : "Collect Earnings"}
        </button>

        <hr style={{ width: "80%", borderColor: "#50E3C2", margin: "8px 0" }} />

        {referralData?.userReferralCode && (
          <div style={{ textAlign: "center" }}>
            <p style={{ margin: "4px 0 2px 0", color: "#fff" }}>Referral Link:</p>
            <span
              style={{
                fontSize: "0.75em",
                color: "#50E3C2",
                textDecoration: "underline",
                cursor: "default",
                pointerEvents: "none",
                userSelect: "text",
                wordBreak: "break-all",
              }}
            >
              {`https://outtradeonline.com/referral?code=${referralData.userReferralCode}`}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}
